import React, { useState } from 'react'
import Tooltip from '@kvika/tooltip'
import { CircleCheck, CircleExclamation } from '@kvika/audur-icons'
import { Colors } from '@kvika/audur-theme'

import {
	StyledInput,
	StyledLabel,
	StyledContainer,
	StyledTopContainer,
	StyledCounter,
	StyledInfo,
} from './styles/InputStyles'

type InputAttributes = React.InputHTMLAttributes<HTMLInputElement>

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
	value?: InputAttributes['value']
	disabled?: InputAttributes['disabled']
	placeholder?: InputAttributes['placeholder']
	onChange?: InputAttributes['onChange']
	width: string
	id?: InputAttributes['id']
	label?: React.ReactNode
	errorText?: string
	error?: boolean
	validText?: string
	valid?: boolean
	testId?: string
	className?: string
	maxLength?: InputAttributes['maxLength']
	fontFamily?: string
	autoFocus?: InputAttributes['autoFocus']
	labelFontSize?: string // TODO: Remove this once we have gone over the Audur UI components and decided on font sizes
	showCharacterCounter?: boolean
	showTooltip?: boolean
	tooltipText?: string
	tooltipIcon?: React.ReactNode
	isLoading?: boolean
	loadingAnimation?: React.ReactNode
}

const getLength = (value: InputAttributes['value']) => {
	if (typeof value === 'string') {
		return value && value.length ? value.length : 0
	}
	if (typeof value === 'number') {
		return value.toString().length || 0
	}
	return 0
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
	(
		{
			value,
			disabled = false,
			placeholder,
			width,
			id,
			label,
			error,
			errorText,
			testId = 'kvika-input',
			className,
			maxLength,
			fontFamily = 'CircularPro',
			autoFocus,
			labelFontSize,
			showCharacterCounter = false,
			showTooltip = false,
			tooltipText,
			tooltipIcon,
			valid,
			validText,
			isLoading,
			loadingAnimation,
			...props
		}: InputProps,
		ref: React.Ref<HTMLInputElement>
	) => {
		const characterCounter = getLength(value)
		const [isKeyboardFocused, setIsKeyboardFocused] = useState<boolean>(false)
		const [isUsingKeyboard, setIsUsingKeyboard] = useState<boolean>(false)

		React.useEffect(() => {
			const handleKeyDown = (e: KeyboardEvent) => {
				if (e.key === 'Tab') {
					setIsUsingKeyboard(true)
				}
			}

			const handleMouseDown = () => {
				setIsUsingKeyboard(false)
			}

			window.addEventListener('keydown', handleKeyDown)
			window.addEventListener('mousedown', handleMouseDown)

			return () => {
				window.removeEventListener('keydown', handleKeyDown)
				window.removeEventListener('mousedown', handleMouseDown)
			}
		}, [])

		const handleFocus = (event: React.FocusEvent<HTMLInputElement, Element>) => {
			if (props.onFocus) {
				props.onFocus(event)
			}
			if (isUsingKeyboard) {
				setIsKeyboardFocused(true)
			}
		}

		const handleBlur = (event: React.FocusEvent<HTMLInputElement, Element>) => {
			if (props.onBlur) {
				props.onBlur(event)
			}
			setIsKeyboardFocused(false)
		}

		return (
			<StyledContainer width={width} className={isKeyboardFocused ? 'keyboard-focused' : ''}>
				{maxLength && showCharacterCounter && (
					<StyledCounter
						data-testid={`${testId}-counter`}
						error={error}
					>{`${characterCounter}/${maxLength}`}</StyledCounter>
				)}
				{error && errorText && (
					<StyledInfo data-testid={`${testId}-error`} title={errorText}>
						<CircleExclamation />
						{errorText}
					</StyledInfo>
				)}
				{!error && valid && validText && (
					<StyledInfo data-testid={`${testId}-valid`} title={validText}>
						<CircleCheck color={Colors.Green} width={24} height={24} />
						{validText}
					</StyledInfo>
				)}
				{isLoading && loadingAnimation && <StyledInfo data-testid={`${testId}-loading`}>{loadingAnimation}</StyledInfo>}

				<StyledInput
					ref={ref}
					data-testid={testId}
					id={id}
					width={width}
					maxLength={maxLength}
					value={value}
					disabled={disabled}
					placeholder={placeholder}
					className={className}
					error={error}
					valid={valid}
					fontFamily={fontFamily}
					{...props}
					autoFocus={autoFocus}
					onFocus={handleFocus}
					onBlur={handleBlur}
				/>
				{label === undefined && maxLength === undefined ? null : (
					<StyledTopContainer>
						{label && (
							<StyledLabel data-testid={`${testId}-label`} htmlFor={id} fontSize={labelFontSize}>
								{label}
							</StyledLabel>
						)}
						{showTooltip && (
							<Tooltip id={`audur-tooltip-${id}`} content={tooltipText}>
								{tooltipIcon}
							</Tooltip>
						)}
					</StyledTopContainer>
				)}
			</StyledContainer>
		)
	}
)
Input.displayName = 'Input'

export default Input
